import { useId, useRef, useState } from 'react';
import { ArrowDown, ArrowUp, GripVertical, ImageOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { moveStickerOrderItem, stickerMutationError, type StickerOrderItem } from '../stickerOrder';

export function StickerArrangeGrid({
  items,
  onSave,
  onCancel,
}: {
  items: StickerOrderItem[];
  onSave: (ids: string[]) => Promise<void>;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState(items);
  const original = useRef(items.map((item) => item.id));
  const [dragging, setDragging] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const instructionsId = useId();
  const changed = draft.some((item, index) => item.id !== original.current[index]);
  const membershipChanged =
    items.length !== original.current.length ||
    items.some((item) => !original.current.includes(item.id));
  const disabled = busy || membershipChanged;

  const move = (id: string, index: number) => {
    if (disabled) return;
    setDraft((current) => moveStickerOrderItem(current, id, index));
    const item = draft.find((item) => item.id === id);
    if (item && index >= 0 && index < draft.length)
      setAnnouncement(`${item.label} moved to position ${index + 1} of ${draft.length}.`);
  };
  const save = async () => {
    if (disabled || !changed) return;
    setBusy(true);
    setError(null);
    try {
      await onSave(draft.map((item) => item.id));
      onCancel();
    } catch (reason) {
      setError(stickerMutationError(reason));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-3 rounded-md border bg-background p-3">
        <p id={instructionsId} className="min-w-48 flex-1 text-sm text-muted-foreground">
          Drag a sticker to reorder it, or focus its card and use the arrow keys. Hidden stickers
          are included. Save applies changes; Cancel discards them.
        </p>
        <Button variant="outline" disabled={busy} onClick={onCancel}>
          Cancel
        </Button>
        <Button disabled={disabled || !changed} onClick={() => void save()}>
          {busy ? 'Saving…' : 'Save order'}
        </Button>
      </div>
      {membershipChanged && (
        <p role="alert" className="text-sm text-destructive">
          This group's stickers changed while arranging. Cancel and start again to include the
          complete group.
        </p>
      )}
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      <p role="status" className="sr-only">
        {announcement}
      </p>
      <div
        role="list"
        aria-label="Sticker draft order"
        className="grid grid-cols-[repeat(auto-fill,minmax(140px,1fr))] gap-3"
      >
        {draft.map((item, index) => (
          <div
            role="listitem"
            key={item.id}
            aria-label={`${item.label}, position ${index + 1} of ${draft.length}`}
            aria-describedby={instructionsId}
            tabIndex={disabled ? -1 : 0}
            draggable={!disabled}
            onKeyDown={(event) => {
              if (event.target !== event.currentTarget || disabled) return;
              if (['ArrowUp', 'ArrowLeft', 'ArrowDown', 'ArrowRight'].includes(event.key)) {
                event.preventDefault();
                move(item.id, index + (['ArrowUp', 'ArrowLeft'].includes(event.key) ? -1 : 1));
              }
            }}
            onDragStart={(event) => {
              if (disabled) {
                event.preventDefault();
                return;
              }
              setDragging(item.id);
              event.dataTransfer.setData('text/plain', item.id);
              event.dataTransfer.effectAllowed = 'move';
            }}
            onDragOver={(event) => {
              if (!disabled && dragging) {
                event.preventDefault();
                event.dataTransfer.dropEffect = 'move';
                setDropTarget(item.id);
              }
            }}
            onDrop={(event) => {
              event.preventDefault();
              if (dragging) move(dragging, index);
              setDragging(null);
              setDropTarget(null);
            }}
            onDragEnd={() => {
              setDragging(null);
              setDropTarget(null);
            }}
            className={cn(
              'rounded-md border bg-background p-2 outline-none focus-visible:ring-2 focus-visible:ring-primary',
              !disabled && 'cursor-grab active:cursor-grabbing',
              dragging === item.id && 'opacity-50',
              dropTarget === item.id &&
                dragging !== item.id &&
                'border-primary ring-2 ring-primary/30',
            )}
          >
            <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
              <span>{index + 1}</span>
              <GripVertical className="size-4" aria-hidden="true" />
            </div>
            <div className="flex h-24 items-center justify-center rounded bg-muted/50">
              {item.thumbnailUrl ? (
                <img
                  src={item.thumbnailUrl}
                  alt={item.label}
                  loading="lazy"
                  draggable={false}
                  className="max-h-full max-w-full object-contain"
                />
              ) : (
                <ImageOff aria-label="No thumbnail" className="size-6 text-muted-foreground" />
              )}
            </div>
            <p className="mt-2 truncate text-xs font-medium" title={item.label}>
              {item.label}
            </p>
            {item.status === 'DELISTED' && <p className="text-xs text-muted-foreground">Hidden</p>}
            <div className="mt-2 flex justify-between gap-2">
              <Button
                variant="outline"
                size="sm"
                aria-label={`Move ${item.label} up`}
                disabled={disabled || index === 0}
                onClick={() => move(item.id, index - 1)}
              >
                <ArrowUp className="size-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                aria-label={`Move ${item.label} down`}
                disabled={disabled || index === draft.length - 1}
                onClick={() => move(item.id, index + 1)}
              >
                <ArrowDown className="size-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
